import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getClassBySlug } from "@/data/classes";

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  organization: "",
  notes: "",
};

export default function WaitlistDialog({ session, onClose }) {
  const open = Boolean(session);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setDone(false);
    }
  }, [open]);

  if (!session) return null;
  const c = getClassBySlug(session.class_slug);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/waitlist", { session_id: session.id, ...form });
      setDone(true);
      toast.success("You're on the waitlist");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="waitlist-dialog">
        {done ? (
          <div className="text-center py-4" data-testid="waitlist-success">
            <CheckCircle2 className="w-14 h-14 text-[#d90429] mx-auto" />
            <DialogHeader className="mt-4">
              <DialogTitle className="font-display text-2xl font-black text-[#0a0a0a] text-center">
                You&apos;re on the waitlist.
              </DialogTitle>
              <DialogDescription className="text-center text-gray-600 mt-2">
                We&apos;ll contact you the moment a seat opens up for{" "}
                {c?.name} on {formatDate(session.date)}.
              </DialogDescription>
            </DialogHeader>
            <button
              onClick={onClose}
              data-testid="waitlist-close-btn"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#0a0a0a] px-6 py-3 text-sm font-bold text-white hover:bg-[#d90429] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="font-mono-label text-[#d90429]">Waitlist</div>
              <DialogTitle className="font-display text-2xl font-black text-[#0a0a0a]">
                {c?.name}
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                {formatDate(session.date)} · {session.start_time} – {session.end_time} · {session.location}
                <br />
                Session is full. Join the waitlist and we&apos;ll contact you if a seat opens.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="waitlist-form">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" name="first_name" value={form.first_name} onChange={handleChange} required />
                <Field label="Last name" name="last_name" value={form.last_name} onChange={handleChange} required />
              </div>
              <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} required />
              <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={handleChange} required />
              <Field label="Organization (optional)" name="organization" value={form.organization} onChange={handleChange} />
              <div>
                <label className="font-mono-label block mb-1" htmlFor="wl-notes">Notes (optional)</label>
                <textarea
                  id="wl-notes"
                  name="notes"
                  rows="2"
                  value={form.notes}
                  onChange={handleChange}
                  data-testid="waitlist-input-notes"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="waitlist-submit-btn"
                className="w-full inline-flex justify-center items-center gap-2 rounded-md bg-[#d90429] px-6 py-3 text-sm font-bold text-white hover:bg-black transition-colors disabled:opacity-60"
              >
                {submitting ? "Adding…" : "Join Waitlist"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, name, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="font-mono-label block mb-1" htmlFor={`wl-${name}`}>
        {label}
      </label>
      <input
        id={`wl-${name}`}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`waitlist-input-${name.replace("_", "-")}`}
        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-[#d90429] focus:ring-2 focus:ring-[#d90429]/20"
      />
    </div>
  );
}
